import { unarchiveComplianceItem } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FeaturedIcon,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import { useNotification } from '../../../../hooks/useNotification';

const UnarchiveFilingModal = ({
  isOpen,
  onClose,
  filing,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  filing: TComplianceItem;
  onSuccess: () => void;
}) => {
  const { setNotification } = useNotification();

  const isSubmitting = useToggle(false);

  const handleClose = () => {
    onClose();
  };

  const onUnarchive = async () => {
    isSubmitting.on();

    const res = await unarchiveComplianceItem(filing.id);

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to unarchive filing.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Filing unarchived successfully.',
    });

    await onSuccess();
    isSubmitting.off();
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-lg items-start border-b-0 pb-0">
          <FeaturedIcon
            size="lg"
            variant="violet"
            icon={<Icons.RefreshCw01 />}
          />
          <div className="space-y-xs">
            <DialogTitle>Unarchive filing?</DialogTitle>
            <DialogDescription>
              Are you sure you want to unarchive{' '}
              <span className="font-bold">{filing.title}</span> for{' '}
              {filing.corporation.companyDisplayName}? This will change the
              filing status to Not Filed.
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogFooter className="border-t-0">
          <Button variant="secondary" onClick={handleClose} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onUnarchive}
            size="md"
            disabled={isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnarchiveFilingModal;
